import React, { useState } from 'react';
import styled from 'styled-components';
import PageBanner from '../components/common/PageBanner';
import ContentSection from '../components/common/ContentSection';
import Button from '../components/common/Button';
import { FaMapMarkerAlt, FaPhoneAlt, FaEnvelope, FaClock, FaCheck } from 'react-icons/fa';
import { theme } from '../styles/GlobalStyles';

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  margin-top: 3rem;

  @media (max-width: 992px) { grid-template-columns: 1fr; }
`;

const ContactInfo = styled.div`
  h3 {
    font-size: 1.7rem;
    margin-bottom: 1.5rem;
    color: ${theme.text};
  }

  > p {
    margin-bottom: 2rem;
    line-height: 1.8;
    color: ${theme.textMuted};
  }
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  gap: 1rem;
`;

const InfoIcon = styled.div`
  width: 40px; height: 40px;
  background: linear-gradient(135deg, rgba(97,93,255,0.2), rgba(61,217,235,0.1));
  border: 1px solid rgba(97,93,255,0.2);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #615DFF;
  font-size: 0.9rem;
  flex-shrink: 0;
`;

const InfoContent = styled.div`
  h4 {
    font-size: 0.9rem;
    font-weight: 600;
    color: ${theme.text};
    margin-bottom: 0.25rem;
  }
  p {
    color: ${theme.textMuted};
    font-size: 0.875rem;
    margin: 0;
    line-height: 1.6;
  }
`;

const ContactForm = styled.div`
  background: ${theme.cardBg};
  border: 1px solid ${theme.border};
  border-radius: 20px;
  padding: 2.5rem;
  backdrop-filter: blur(14px);
  box-shadow: ${theme.cardShadow};

  h3 {
    font-size: 1.7rem;
    margin-bottom: 1.5rem;
    color: ${theme.text};
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  font-size: 0.875rem;
  color: ${theme.textMuted};
`;

const inputBase = `
  width: 100%;
  padding: 0.75rem 1rem;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 10px;
  font-size: 0.92rem;
  color: #F0F0FF;
  transition: border-color 0.2s, box-shadow 0.2s;
  outline: none;
  font-family: inherit;

  &::placeholder { color: rgba(240,240,255,0.25); }

  &:focus {
    border-color: rgba(97,93,255,0.6);
    box-shadow: 0 0 0 3px rgba(97,93,255,0.12);
  }
`;

const Input    = styled.input`${inputBase}`;
const Select   = styled.select`${inputBase} cursor: pointer; background-color: #0D0E1E;`;
const TextArea = styled.textarea`${inputBase} resize: vertical; min-height: 140px;`;

const SuccessMessage = styled.div`
  background: rgba(61,217,235,0.08);
  border: 1px solid rgba(61,217,235,0.25);
  color: #3DD9EB;
  padding: 1rem 1.25rem;
  border-radius: 10px;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.9rem;
  font-weight: 500;
`;

const contactItems = [
  { icon: <FaMapMarkerAlt />, label: 'Our Location', lines: ['123 Product Street, Innovation District', 'New York, NY 10001, USA'] },
  { icon: <FaPhoneAlt />,     label: 'Call Us',       lines: ['+1 (555) 123-4567', '+1 (555) 987-6543'] },
  { icon: <FaEnvelope />,     label: 'Email Us',      lines: ['info@prodseal.com', 'support@prodseal.com'] },
  { icon: <FaClock />,        label: 'Office Hours',  lines: ['Monday – Friday: 9:00 AM – 6:00 PM', 'Saturday – Sunday: Closed'] },
];

const ContactPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  const handleSubmit = (e) => { e.preventDefault(); setSubmitted(true); };

  return (
    <>
      <PageBanner
        title="Contact Us"
        subtitle="Let's Secure Your Products Today. Reach out to learn how Prodseal can protect your brand."
      />

      <ContentSection>
        <ContactGrid>
          <ContactInfo>
            <h3>Get In Touch</h3>
            <p>
              Have questions about our product authentication solutions? Want to see a demo?
              Our team is ready to help you protect your brand and build consumer trust.
            </p>
            {contactItems.map(({ icon, label, lines }) => (
              <InfoItem key={label}>
                <InfoIcon>{icon}</InfoIcon>
                <InfoContent>
                  <h4>{label}</h4>
                  {lines.map((l) => <p key={l}>{l}</p>)}
                </InfoContent>
              </InfoItem>
            ))}
          </ContactInfo>

          <ContactForm>
            <h3>Send Us a Message</h3>
            {submitted && (
              <SuccessMessage>
                <FaCheck />
                Your message has been sent successfully. We'll get back to you soon!
              </SuccessMessage>
            )}
            <form onSubmit={handleSubmit}>
              <FormGroup>
                <Label htmlFor="name">Full Name *</Label>
                <Input id="name" name="name" type="text" required placeholder="Jane Smith" value={formData.name} onChange={handleChange} />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="email">Email Address *</Label>
                <Input id="email" name="email" type="email" required placeholder="jane@company.com" value={formData.email} onChange={handleChange} />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="phone">Phone Number</Label>
                <Input id="phone" name="phone" type="tel" placeholder="+1 (555) 000-0000" value={formData.phone} onChange={handleChange} />
              </FormGroup>
              <FormGroup>
                <Label htmlFor="subject">Subject *</Label>
                <Select id="subject" name="subject" required value={formData.subject} onChange={handleChange}>
                  <option value="">Please select</option>
                  <option value="Demo Request">Demo Request</option>
                  <option value="Pricing Information">Pricing Information</option>
                  <option value="Technical Support">Technical Support</option>
                  <option value="Partnership Inquiry">Partnership Inquiry</option>
                  <option value="Other">Other</option>
                </Select>
              </FormGroup>
              <FormGroup>
                <Label htmlFor="message">Message *</Label>
                <TextArea id="message" name="message" required placeholder="Tell us about your needs..." value={formData.message} onChange={handleChange} />
              </FormGroup>
              <Button primary type="submit" style={{ width: '100%', justifyContent: 'center' }}>
                Send Message
              </Button>
            </form>
          </ContactForm>
        </ContactGrid>
      </ContentSection>
    </>
  );
};

export default ContactPage;
